import React from 'react';
import Image from 'components/image';
import PoorText from 'components/poor-text';
import HeadingCropper from 'components/heading-cropper';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { imageSourceBreakpoints } from 'js/breakpoints';

// eslint-disable-next-line view-models/no-errors
export const TeaserWidths = {
  oneThird: '1-3',
  twoThird: '2-3',
  full: 'full-width',
};

const getImageSources = teaserWidth => {
  switch (teaserWidth) {
    case TeaserWidths.oneThird:
      return [
        { width: 390, height: 219, media: imageSourceBreakpoints.xs },
        { width: 292, height: 188, media: imageSourceBreakpoints.s },
        { width: 325, height: 209, media: imageSourceBreakpoints.m },
        { width: 542, height: 404, media: imageSourceBreakpoints.l },
      ];

    case TeaserWidths.twoThird:
      return [
        { width: 390, height: 219, media: imageSourceBreakpoints.xs },
        { width: 190, height: 302, media: imageSourceBreakpoints.s },
        { width: 300, height: 390, media: imageSourceBreakpoints.m },
        { width: 500, height: 630, media: imageSourceBreakpoints.l },
      ];

    case TeaserWidths.full:
      return [
        { width: 390, height: 219, media: imageSourceBreakpoints.xs },
        { width: 364, height: 292, media: imageSourceBreakpoints.s },
        { width: 492, height: 389, media: imageSourceBreakpoints.m },
        { width: 820, height: 625, media: imageSourceBreakpoints.l },
      ];

    default:
      return [];
  }
};

const Teaser = ({
  className,
  categories,
  teaserWidth,
  useWiderImageOnHorizontalTwoThirdTeaser = false,
  url,
  image,
  heading,
  headingTagLevel = 3,
  introHtml,
  backgroundColor,
  preloadImage,
}) => {
  const horizontalMode = teaserWidth !== TeaserWidths.oneThird;
  const fullWidthMode = teaserWidth === TeaserWidths.full;

  const RootElement = url ? 'a' : 'div';
  const hideIntro = teaserWidth === TeaserWidths.oneThird && image;
  const HeadingTag = `h${headingTagLevel}`;

  return (
    <div
      style={{ backgroundColor }}
      className={cn('teaser', {
        teaser_horizontal: horizontalMode,
        'teaser_horizontal-wider-image':
          horizontalMode && useWiderImageOnHorizontalTwoThirdTeaser,
        teaser_vertical: !horizontalMode,
        'teaser_full-width': fullWidthMode,
        [className]: className,
      })}
    >
      <RootElement
        href={url}
        {...(url && {
          className: 'teaser__link',
        })}
      >
        <div className="teaser__container">
          {image && (
            <div className="teaser__media">
              <Image
                {...image}
                sources={getImageSources(teaserWidth)}
                {...(!preloadImage && { loading: 'lazy' })}
                {...(preloadImage && { fetchPriority: 'high' })}
                className={cn('teaser__image', {
                  'image_size_teaser-horizontal': horizontalMode,
                  'image_size_teaser-vertical': !horizontalMode,
                })}
              />
            </div>
          )}
          <div className="teaser__content">
            {categories && categories.length > 0 && (
              <ul className="teaser__categories">
                {categories.map((item, index) => (
                  <li className="teaser__categories-item" key={index}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {heading && (
              <HeadingTag
                className={cn('teaser__heading', {
                  teaser__heading_large: fullWidthMode && !introHtml && !image,
                })}
              >
                <HeadingCropper
                  text={heading}
                  maxLinesMobile={
                    className === 'teaser_in-two-columns-view' ? 6 : 2
                  }
                  maxLinesDesktop={2}
                />
              </HeadingTag>
            )}
            {introHtml && !hideIntro && (
              <PoorText
                className="teaser__text poor-text_in-teaser"
                text={introHtml}
              />
            )}
          </div>
        </div>
      </RootElement>
    </div>
  );
};

Teaser.propTypes = {
  teaserWidth: PropTypes.oneOf(Object.values(TeaserWidths)),
  useWiderImageOnHorizontalTwoThirdTeaser: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  url: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  heading: PropTypes.string.isRequired,
  headingTagLevel: PropTypes.oneOf([2, 3]),
  introHtml: PropTypes.string,
  backgroundColor: PropTypes.string.isRequired,
  preloadImage: PropTypes.bool,
};

Teaser.viewModelMeta = {
  headingTagLevel: 'ignore',
  className: 'ignore',
  teaserWidth: 'ignore',
  useWiderImageOnHorizontalTwoThirdTeaser: 'ignore',
  preloadImage: 'ignore',
};

// eslint-disable-next-line view-models/no-errors
export default Teaser;
